import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  TrendingUp,
  Wallet,
  FileText,
  Bell, 
  Shield, 
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Briefcase,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [portfolioHistory, setPortfolioHistory] = useState([]);
  const [loanStatusBreakdown, setLoanStatusBreakdown] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingMaturities, setUpcomingMaturities] = useState([]);
  const [error, setError] = useState(null);

  const getProfileParam = () => {
    const pid = localStorage.getItem('activeProfileId');
    return pid ? `?profile_id=${pid}` : '';
  };

  const fetchDashboardData = async (currentToken) => {
    if (!currentToken) return;
    
    setLoading(true);
    setError(null);
    
    const headers = { Authorization: `Bearer ${currentToken}` };
    const pq = getProfileParam();
    
    try {
      const [summaryRes, historyRes, statusRes, activityRes, maturitiesRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/summary${pq}`, { headers }),
        axios.get(`${API_URL}/dashboard/portfolio-history${pq}`, { headers }),
        axios.get(`${API_URL}/dashboard/loan-status-breakdown${pq}`, { headers }),
        axios.get(`${API_URL}/dashboard/recent-activity`, { headers }),
        axios.get(`${API_URL}/dashboard/upcoming-maturities${pq}`, { headers })
      ]);
      
      setSummary(summaryRes.data);
      setPortfolioHistory(historyRes.data);
      setLoanStatusBreakdown(statusRes.data);
      setRecentActivity(activityRes.data);
      setUpcomingMaturities(maturitiesRes.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboardData(token);
    }
  }, [token]);

  // Listen for profile switch events from the Header
  useEffect(() => {
    const handleProfileSwitch = () => {
      if (token) fetchDashboardData(token);
    };
    window.addEventListener('profileSwitch', handleProfileSwitch);
    return () => window.removeEventListener('profileSwitch', handleProfileSwitch);
  }, [token]);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'R 0.00';
    return new Intl.NumberFormat('en-ZA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount).replace(/^/, 'R ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short'
    });
  };

  // Chart colors — pie slices use color from backend when provided,
  // else cycle through this palette.
  const PIE_PALETTE = ['#1D2D2A', '#18A37C', '#0DCAF0', '#FFC107', '#DC3545',
    '#6F42C1', '#20C997', '#FD7E14', '#0D6EFD', '#6C757D', '#198754', '#E83E8C'];

  const pieData = Object.entries(loanStatusBreakdown)
    .filter(([, value]) => {
      const amount = typeof value === 'object' ? (value.amount || 0) : (value || 0);
      return amount > 0;
    })
    .map(([key, value], idx) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value: typeof value === 'object' ? (value.amount || 0) : (value || 0),
      count: typeof value === 'object' ? (value.count || 0) : 0,
      color: (typeof value === 'object' && value.color) || PIE_PALETTE[idx % PIE_PALETTE.length],
    }));

  const chartData = portfolioHistory.map(item => ({
    month: item.month,
    value: item.portfolio_value
  }));
  
  const hasChartData = chartData.length > 0 && chartData.some(d => d.value > 0);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'distribution': return <ArrowDownRight className="h-5 w-5 text-green-600" />;
      case 'investment': return <ArrowUpRight className="h-5 w-5 text-blue-600" />;
      case 'withdrawal': return <ArrowDownRight className="h-5 w-5 text-amber-600" />;
      case 'bank_interest': return <ArrowUpRight className="h-5 w-5 text-green-600" />;
      case 'deal_purchase': return <ArrowDownRight className="h-5 w-5 text-blue-600" />;
      case 'deal_repayment': return <ArrowUpRight className="h-5 w-5 text-blue-600" />;
      case 'statement': return <FileText className="h-5 w-5 text-purple-600" />;
      case 'document': return <FileText className="h-5 w-5 text-indigo-600" />;
      case 'notice': return <Bell className="h-5 w-5 text-amber-600" />;
      default: return <Activity className="h-5 w-5 text-gray-600" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'distribution': return 'bg-green-100';
      case 'investment': return 'bg-blue-100';
      case 'withdrawal': return 'bg-amber-100';
      case 'bank_interest': return 'bg-green-100';
      case 'deal_purchase': return 'bg-blue-100';
      case 'deal_repayment': return 'bg-blue-100';
      case 'statement': return 'bg-purple-100';
      case 'document': return 'bg-indigo-100';
      case 'notice': return 'bg-amber-100';
      default: return 'bg-gray-100';
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm text-[#6C757D]">{label}</p>
          <p className="text-lg font-semibold text-[#1D2D2A]">
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-8" data-testid="dashboard-loading">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <AlertCircle className="h-12 w-12 text-[#DC3545]" />
        <p className="text-[#6C757D]">{error}</p>
        <Button onClick={() => fetchDashboardData(token)} className="bg-[#1D2D2A] hover:bg-[#243D37]">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  const hasData = summary && summary.total_invested > 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="animate-fade-in flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">
            Welcome back, {user?.first_name}
          </h1>
          <p className="text-[#6C757D] mt-1">
            Here's an overview of your investment portfolio
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => fetchDashboardData(token)}
          className="text-[#6C757D] hover:text-[#1D2D2A]"
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Funds Invested (net) */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" data-testid="total-invested-card">
          <CardHeader className="pb-2">
            <CardDescription className="text-[#6C757D] flex items-center gap-2">
              <Wallet className="h-4 w-4" />
              Total Funds Invested
            </CardDescription>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-bold text-[#212529]" data-testid="total-invested-value">
              {formatCurrency(summary?.net_capital_invested ?? summary?.total_invested)}
            </span>
            <p className="text-xs text-[#6C757D] mt-1 flex items-center gap-3">
              <span className="text-[#18A37C]">Investments {formatCurrency(summary?.capital_invested || 0)}</span>
              <span className="text-[#B58900]">Withdrawals {formatCurrency(summary?.capital_withdrawn || 0)}</span>
            </p>
          </CardContent>
        </Card>

        {/* Current Portfolio Value */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 bg-gradient-to-br from-[#1D2D2A] to-[#315C52] text-white animate-fade-in stagger-1" data-testid="portfolio-value-card">
          <CardHeader className="pb-2">
            <CardDescription className="text-white/80 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Current Portfolio Value
              {summary?.values_as_of && (
                <span className="ml-auto text-[10px] text-white/60" data-testid="portfolio-value-as-of">as of {formatDate(summary.values_as_of)}</span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold font-['Playfair_Display']" data-testid="portfolio-value">
                {formatCurrency(summary?.current_portfolio_value)}
              </span>
            </div>
            <p className="text-xs text-white/70 mt-1">
              Loans {formatCurrency(summary?.loan_portfolio_value || 0)} + Cash {formatCurrency(summary?.bank_balance || 0)}
              {Math.abs(summary?.deal_room_balance || 0) > 0.005 && (
                <>
                  {' '}+ <span data-testid="deal-room-balance-inline">Deal Room {formatCurrency(summary.deal_room_balance)}</span>
                </>
              )}
              {Math.abs(summary?.cash_in_transit || 0) > 0.005 && (
                <>
                  {' '}+ <span data-testid="cash-in-transit-inline">Cash in Transit {formatCurrency(summary.cash_in_transit)}</span>
                </>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Weighted Average Yield */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in stagger-2" data-testid="yield-card">
          <CardHeader className="pb-2">
            <CardDescription className="text-[#6C757D] flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Weighted Avg. Yield
            </CardDescription>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-bold text-[#1D2D2A]" data-testid="yield-value">
              {summary?.weighted_average_yield || 0}%
            </span>
            <p className="text-xs text-[#6C757D] mt-1">Per annum</p>
          </CardContent>
        </Card>

      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Portfolio Performance Chart */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 lg:col-span-2 animate-fade-in stagger-4" data-testid="performance-chart-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#212529] font-['Manrope']">
              Portfolio Performance
            </CardTitle>
            <CardDescription>Monthly portfolio value over the last 12 months</CardDescription>
          </CardHeader>
          <CardContent>
            {hasChartData ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#6C757D', fontSize: 12 }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      return months[parseInt(month) - 1];
                    }}
                  />
                  <YAxis 
                    tick={{ fill: '#6C757D', fontSize: 12 }}
                    tickFormatter={(value) => `R${(value / 1000000).toFixed(1)}M`}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#1D2D2A" 
                    strokeWidth={3}
                    dot={{ fill: '#1D2D2A', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: '#18A37C' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#6C757D]">
                No portfolio history data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Loan Status Breakdown */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in stagger-5" data-testid="status-breakdown-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#212529] font-['Manrope']">
              Portfolio Allocation
            </CardTitle>
            <CardDescription>By category</CardDescription>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [formatCurrency(value), `${name} (${props.payload.count || 0} loans)`]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E9ECEF' }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value) => <span className="text-sm text-[#212529]">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#6C757D]">
                No loan data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Compliance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 lg:col-span-2 animate-fade-in" data-testid="recent-activity-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-[#212529] font-['Manrope'] flex items-center gap-2">
                <Activity className="h-5 w-5 text-[#1D2D2A]" />
                Recent Activity
              </CardTitle>
              <Button 
                variant="ghost" 
                className="text-[#18A37C] hover:text-[#1D2D2A] text-sm"
                onClick={() => navigate('/notices')}
                data-testid="view-all-activity-btn"
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentActivity.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.map((activity) => (
                  <div 
                    key={activity.id}
                    className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0 rounded-lg px-2"
                    data-testid={`activity-item-${activity.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActivityColor(activity.activity_type)}`}>
                        {getActivityIcon(activity.activity_type)}
                      </div>
                      <div>
                        <p className="font-medium text-[#212529]">{activity.title}</p>
                        <p className="text-sm text-[#6C757D]">{activity.description}</p>
                        <p className="text-xs text-[#ADB5BD] mt-0.5">{formatDate(activity.created_at)}</p>
                      </div>
                    </div>
                    {activity.amount && (() => {
                      const isInflow = ['distribution', 'bank_interest', 'deal_repayment'].includes(activity.activity_type);
                      return (
                        <span className={`font-semibold ${isInflow ? 'text-green-600' : 'text-[#212529]'}`}>
                          {isInflow ? '+' : ''}{formatCurrency(activity.amount)}
                        </span>
                      );
                    })()}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-[#6C757D]">
                No recent activity
              </div>
            )}
          </CardContent>
        </Card>

        {/* FICA Status */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" data-testid="fica-status-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#212529] font-['Manrope']">
              Compliance Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  summary?.fica_status === 'compliant' ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  <Shield className={`h-5 w-5 ${
                    summary?.fica_status === 'compliant' ? 'text-green-600' : 'text-amber-600'
                  }`} />
                </div>
                <div>
                  <p className="font-medium text-[#212529]">FICA Status</p>
                  <Badge 
                    className={`mt-1 ${
                      summary?.fica_status === 'compliant' 
                        ? 'bg-[#198754]/10 text-[#198754] border border-[#198754]/20' 
                        : 'bg-[#FFC107]/10 text-[#B58900] border border-[#FFC107]/20'
                    }`}
                    data-testid="fica-status-badge"
                  >
                    {summary?.fica_status === 'compliant' ? 'Verified' : 'Pending Review'}
                  </Badge>
                </div>
              </div>
            </div>
            {summary?.fica_status !== 'compliant' && (
              <Button 
                className="w-full mt-4 bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                onClick={() => navigate('/kyc')}
                data-testid="complete-kyc-btn"
              >
                Complete KYC
              </Button>
            )}
            
            {/* Quick Actions */}
            <div className="mt-6 pt-4 border-t border-gray-100">
              <p className="text-sm font-medium text-[#212529] mb-3">Quick Actions</p>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="h-auto py-3 flex flex-col items-center gap-1 border-gray-200 hover:border-[#18A37C] hover:bg-[#18A37C]/5"
                  onClick={() => navigate('/portfolio')}
                  data-testid="quick-portfolio-btn"
                >
                  <Briefcase className="h-4 w-4 text-[#1D2D2A]" />
                  <span className="text-xs">Portfolio</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="h-auto py-3 flex flex-col items-center gap-1 border-gray-200 hover:border-[#18A37C] hover:bg-[#18A37C]/5"
                  onClick={() => navigate('/statements')}
                  data-testid="quick-statements-btn"
                >
                  <FileText className="h-4 w-4 text-[#1D2D2A]" />
                  <span className="text-xs">Statements</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Maturities */}
      <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" data-testid="upcoming-maturities-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-[#212529] font-['Manrope']">
                Upcoming Maturities
              </CardTitle>
              <CardDescription>Investments maturing in the next 90 days</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              className="text-[#18A37C] hover:text-[#1D2D2A] text-sm"
              onClick={() => navigate('/portfolio')}
              data-testid="view-all-maturities-btn"
            >
              View All Investments
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {upcomingMaturities.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[#6C757D]">Loan Reference</TableHead>
                  <TableHead className="text-[#6C757D]">Borrower</TableHead>
                  <TableHead className="text-[#6C757D]">Maturity Date</TableHead>
                  <TableHead className="text-[#6C757D] text-right">Outstanding Amount</TableHead>
                  <TableHead className="text-[#6C757D] text-right">Days to Maturity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingMaturities.map((loan) => (
                  <TableRow 
                    key={loan.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/portfolio`)}
                    data-testid={`maturity-row-${loan.id}`}
                  >
                    <TableCell className="font-medium text-[#1D2D2A]">{loan.loan_reference}</TableCell>
                    <TableCell className="text-[#212529]">{loan.borrower_name}</TableCell>
                    <TableCell className="text-[#212529]">{formatDate(loan.maturity_date)}</TableCell>
                    <TableCell className="text-right font-medium text-[#212529]">
                      {formatCurrency(loan.outstanding_balance)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                        {loan.days_to_maturity} days
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-[#6C757D]">
              No investments maturing in the next 90 days
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
